import {
  AppShell,
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SuperAdminBanner,
  SyncIndicator,
  cn,
  initials,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { Brain, Building2, FileSearch, LayoutDashboard, LogOut } from 'lucide-react';
import { useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';


import { fetchMe, logout } from '../../api/auth.js';
import { useSyncStore } from '../../stores/sync-store.js';

const NAV = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/orgs', label: 'Organizations', icon: Building2 },
  { to: '/admin/ai', label: 'AI usage', icon: Brain },
  { to: '/admin/audit', label: 'Audit log', icon: FileSearch },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ['admin-me'], queryFn: fetchMe });
  const syncState = useSyncStore((s) => s.state);

  useEffect(() => {
    document.title = 'SiteWise — admin console';
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/admin/login', { replace: true });
  }

  return (
    <AppShell
      isSuperAdmin
      topBanner={<SuperAdminBanner />}
      sidebarHeader={
        <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-accent text-xs font-bold text-accent-fg">
            S
          </div>
          <span className="hidden text-sm font-semibold tracking-tight text-sidebar-fg xl:inline">
            SiteWise
          </span>
        </Link>
      }
      topHeader={
        <>
          <div className="flex flex-1 items-center gap-3">
            <span className="text-sm font-semibold text-fg">Operator</span>
          </div>
          {meQuery.data ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Admin menu">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback>
                      {initials(meQuery.data.user.name || meQuery.data.user.email)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{meQuery.data.user.name}</div>
                  <div className="text-xs text-fg-muted">{meQuery.data.user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout} className="text-danger">
                  <LogOut className="h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      }
      nav={
        <ul className="space-y-0.5">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex h-10 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors max-xl:justify-center',
                    isActive
                      ? 'bg-sidebar-hover text-accent'
                      : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg',
                  )
                }
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" />
                <span className="hidden truncate xl:inline">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      }
      sidebarFooter={
        <Button
          variant="ghost"
          className="w-full justify-start text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg max-xl:px-0"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      }
      overlays={<SyncIndicator state={syncState} />}
    >
      <Outlet />
    </AppShell>
  );
}
